// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useSize from "@hardwoods/App/Hooks/useSize";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@hardwoods/Overrides/Components/SmallHeadingAndText";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { partNumberStyle } from "@hardwoods/Overrides/Widgets/ProductList/helper";
import StickyNote from "@hardwoods/XDL/Icons/StickyNote";
import color from "@hardwoods/XDL/Utilities/color";
import { iconLinkCommonStyles } from "@hardwoods/XDL/Utilities/iconButtonCommonStyles";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    shouldShowNotes?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    // XNG-Changes
    showLabel?: boolean;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: LinkPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    textAreaWrapper?: InjectableCss;
}

// XNG-Changes: update Styling
export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
            ${({ marginBottom }: any) =>
                css`
                    ${marginBottom && `margin-bottom: ${marginBottom}px;`};
                `}

            ${xScreen.lte(cartLineBreakpoints.sm)} {
                margin-bottom: 0;
            }
        `,
    },
    textAreaWrapper: {
        css: css`
            position: absolute;
            top: 32px;
            left: 147px;
            right: 0;
            width: auto;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                left: 0;
            }
            ${xScreen.lte(cartLineBreakpoints.sm)} {
                position: relative;
                top: 0;
                left: 0;
                right: 0;
                width: 100%;
            }
        `,
    },
    label: {
        ...iconLinkCommonStyles(StickyNote, {
            variantType: "secondary",
            sizeVariant: "small",
            css: css`
                margin-bottom: 10px;
                &.hideLineNotes {
                    &,
                    & ${TypographyStyle}, & ${IconWrapper} {
                        color: ${color("text.main")};
                    }
                }
            `,
        }),
    },
    editableNotesTextArea: {
        cssOverrides: {
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                min-height: 58px;
                width: auto;
            `,
        },
    },
    readOnlyNotesText: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                > ${TypographyStyle}:first-child {
                    flex-shrink: 0;
                }
            `,
        },
        heading: {
            variant: "legend",
            ...partNumberStyle.label,
            weight: "bold",
            transform: "capitalize",
        },
        text: {
            ...mergeWithCss(partNumberStyle.value, {
                weight: "normal",
            }),
        },
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    shouldShowNotes = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    showLabel,
}) => {
    const [styles] = React.useState(() => mergeWithCss(cartLineNotesStyles, extendedStyles));
    const [showLineNotes, setShowLineNotes] = useState(shouldShowNotes);
    const ref = useRef(null);
    const wrapperSize = useSize(ref);

    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
    };

    const [notes, setNotes] = useState(cartLine.notes);

    // <XNG-Changes> To update notes in text area when cartline notes change
    // It's OOTB issue
    useEffect(() => {
        if (cartLine.notes !== notes) {
            setNotes(cartLine.notes);
        }
    }, [cartLine]);
    // </XNG-Changes>

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        onNotesChange(notes);
    };

    // XNG-Changes: update messages
    const heading = showLineNotes
        ? "Hide Customer Comments"
        : cartLine.notes.length === 0
        ? "Add Customer Comments"
        : "Show Customer Comments";

    // XNG-Changes: add className to the Link and PlaceHolder to the TextArea
    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper} {...{ marginBottom: wrapperSize?.height ?? 0 }}>
                <Link
                    {...styles.label}
                    onClick={lineNotesClickHandler}
                    className={showLineNotes ? "hideLineNotes" : "showLineNotes"}
                    data-test-selector="cartline_notesShowHideButton"
                >
                    {translate(heading)}
                </Link>
                <StyledWrapper ref={ref} {...styles.textAreaWrapper}>
                    {showLineNotes && (
                        <TextArea
                            {...styles.editableNotesTextArea}
                            value={notes}
                            id={`notes-${cartLine.id}`}
                            disabled={!cart.canModifyOrder}
                            onChange={changeHandler}
                            onBlur={blurHandler}
                            data-test-selector="cartline_notes"
                            required={cartLine.properties?.itemSpecIsRequired === "True"}
                            error={shouldShowNotes ? "This field is required." : ""}
                            placeholder={cartLine.properties?.itemSpecPlacholder || translate(
                                "Please add any additional notes or requirements for your order here",
                            )}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (!cartLine.notes) {
        return null;
    }

    // XNG-Changes: add showLabel
    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyNotesText}
            heading={showLabel ? label || translate("Customer Notes") : undefined}
            text={cartLine.notes}
        />
    );
};

export default withCartLine(CartLineNotes);
